import clsx from 'clsx'
import PropTypes from 'prop-types'
import React from 'react'
import { Col, Row } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import PhoneInput from 'react-phone-input-2'
import { PW_MANAGER_OFF } from '../../util/inputAttrs'

const propTypes = {
  /**
   * id of the field
   */
  id: PropTypes.string.isRequired,
  /**
   * Name of the field
   * If no name is passed id is used as name
   */
  name: PropTypes.string,
  /**
   * Title of the field. Must be a FormattedMessage id
   */
  title: PropTypes.string,
  /**
   * Callback called when an option is selected
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Value of the field
   */
  value: PropTypes.string,
  /**
   * validation errors messages
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * Class to style the title
   */
  titleClassName: PropTypes.string,
  /**
   * Class of the container wrapper
   */
  containerClassName: PropTypes.string,
  /**
   * Indicates if field is required
   */
  required: PropTypes.bool,
  /**
   * Input placeholder
   */
  placeholder: PropTypes.string,
  /**
   * If true, displays label and input side-by-side (like PropertyField)
   */
  inline: PropTypes.bool,
  /**
   * If true, disables the country code selector. If not provided, defaults to !value (disabled when empty)
   */
  disableCountryCode: PropTypes.bool,
  /**
   * Extra attributes passed to the inner phone input (react-phone-input-2)
   */
  inputProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
}

function getPhoneInputProps(id, name, inputProps) {
  return {
    ...PW_MANAGER_OFF,
    autoComplete: 'tel',
    type: 'tel',
    id,
    name: name || id,
    ...inputProps,
  }
}

function PhoneField(props) {
  const {
    id,
    name = null,
    title = null,
    placeholder = '',
    onChange,
    value = null,
    errors = [],
    titleClassName = '',
    containerClassName = '',
    required = false,
    inline = false,
    disableCountryCode = false,
    inputProps,
    ...rest
  } = props ?? {}

  const phoneInputProps = getPhoneInputProps(id, name, inputProps)

  if (inline) {
    return (
      <Row className={clsx('mb-2 align-items-center', containerClassName)}>
        {title && (
          <Col sm="4" className={clsx('text-bold text-muted', titleClassName)}>
            <FormattedMessage id={title} />
            {required && <span className="font-size-regular text-color-danger">*</span>}
          </Col>
        )}
        <Col sm={title ? '8' : '12'} style={{ marginLeft: title ? '-4px' : '0' }}>
          <PhoneInput
            {...rest}
            inputProps={phoneInputProps}
            preferredCountries={['ca', 'us', 'gb']}
            country="ca"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disableCountryCode={disableCountryCode ?? !value}
          />

          {errors?.map((errorMessage) => (
            <div key={errorMessage} className="text-color-danger mt-2">
              {errorMessage}
            </div>
          ))}
        </Col>
      </Row>
    )
  }

  return (
    <div className={clsx('mb-3', containerClassName)}>
      {title && (
        <p className={clsx('mb-2', titleClassName)}>
          <FormattedMessage id={title} />
          {required && <span className="font-size-regular text-color-danger">*</span>}
        </p>
      )}

      <PhoneInput
        {...rest}
        inputProps={phoneInputProps}
        preferredCountries={['ca', 'us', 'gb']}
        country="ca"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disableCountryCode={disableCountryCode ?? !value}
      />

      {errors?.map((errorMessage) => (
        <div key={errorMessage} className="text-color-danger mt-2">
          {errorMessage}
        </div>
      ))}
    </div>
  )
}

PhoneField.propTypes = propTypes

export default PhoneField
